import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  IconButton,
  Paper,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useNotification } from '../contexts/NotificationContext';
import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

const emptyConnection = () => ({
  id: '',
  name: 'Nueva conexión',
  isDefault: false,
  engine: 'mssql',
  host: '',
  port: 1433,
  user: '',
  password: '',
  database: '',
  allowedDatabases: '',
  encrypt: false,
  trustServerCertificate: true,
  ssl: false,
  passwordSet: false,
});

const DataConnectionAdmin = ({ open, getAuthHeaders, embedded = false }) => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [listingId, setListingId] = useState(null);
  const [error, setError] = useState(null);
  const [testOk, setTestOk] = useState(null);
  const [databasesById, setDatabasesById] = useState({});
  const [connections, setConnections] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const loadConfigMutation = useMutation({
    mutationFn: (headers) => axios.get('/api/admin/runtime-config', { headers }),
  });

  useEffect(() => {
    if (!open || !getAuthHeaders) return;
    setError(null);
    setTestOk(null);
    setLoading(true);
    const headers = getAuthHeaders();
    if (!headers) {
      setError('No hay credenciales de sesión');
      setLoading(false);
      return;
    }
    loadConfigMutation
      .mutateAsync(headers)
      .then((res) => {
        if (res.data.success) {
          const list = (res.data.dataConnections || []).map((c) => ({
            ...emptyConnection(),
            ...c,
            password: '',
            port: c.port || (c.engine === 'timescaledb' ? 5432 : 1433),
          }));
          setConnections(list.length > 0 ? list : [emptyConnection()]);
          const def = list.find((c) => c.isDefault) || list[0];
          setActiveId(def?.id || list[0]?.id || null);
        } else {
          setError(res.data.error || 'Error al cargar');
        }
      })
      .catch((err) => {
        setError(err.response?.data?.error || err.message || 'Error al cargar');
      })
      .finally(() => setLoading(false));
  }, [open, getAuthHeaders]);

  const active = connections.find((c) => c.id === activeId) || connections[0];

  const updateActive = (patch) => {
    if (!active) return;
    setConnections((prev) =>
      prev.map((c) => (c.id === active.id ? { ...c, ...patch } : c))
    );
  };

  const draftForTest = (conn) => ({
    id: conn.id,
    engine: conn.engine,
    host: conn.host,
    port: conn.port,
    user: conn.user,
    password: conn.password || undefined,
    encrypt: conn.encrypt,
    trustServerCertificate: conn.trustServerCertificate,
    ssl: conn.ssl,
  });

  const testConnectionMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/admin/data-connection/test', body, { headers }),
  });
  const listDatabasesMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.post('/api/admin/data-connection/databases', body, { headers }),
  });

  const handleTest = async (conn) => {
    const headers = getAuthHeaders();
    if (!headers) {
      showError('No hay credenciales de sesión');
      return;
    }
    setTestingId(conn.id);
    setError(null);
    setTestOk(null);
    try {
      const draft = draftForTest(conn);
      const res = await testConnectionMutation.mutateAsync({ body: draft, headers });
      if (res.data.success) {
        const info = res.data.serverInfo;
        const verLabel = info?.fullVersion
          ? String(info.fullVersion).split('\n')[0].trim()
          : info?.version || 'servidor detectado';
        setTestOk(`Conexión "${conn.name}" exitosa — ${verLabel}`);
        if (info) {
          updateActive({ serverInfo: info, engine: info.engine || conn.engine });
        }
        setListingId(conn.id);
        const listRes = await listDatabasesMutation.mutateAsync({
          body: { ...draft, allowedDatabases: conn.allowedDatabases },
          headers,
        });
        if (listRes.data.success) {
          setDatabasesById((prev) => ({ ...prev, [conn.id]: listRes.data.databases || [] }));
        }
      } else {
        setError(res.data.error || 'Error al conectar');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Error al conectar');
    } finally {
      setTestingId(null);
      setListingId(null);
    }
  };

  const handleAdd = () => {
    const id = `conn-${Date.now().toString(36)}`;
    const next = {
      ...emptyConnection(),
      id,
      name: `Conexión ${connections.length + 1}`,
      isDefault: connections.length === 0,
    };
    setConnections((prev) => [...prev, next]);
    setActiveId(id);
  };

  const handleRemove = (id) => {
    if (connections.length <= 1) {
      showError('Debe existir al menos una conexión');
      return;
    }
    setConnections((prev) => {
      const next = prev.filter((c) => c.id !== id);
      if (!next.some((c) => c.isDefault) && next.length > 0) {
        next[0].isDefault = true;
      }
      return next;
    });
    if (activeId === id) {
      setActiveId(connections.find((c) => c.id !== id)?.id || null);
    }
  };

  const setDefault = (id) => {
    setConnections((prev) => prev.map((c) => ({ ...c, isDefault: c.id === id })));
  };

  const onEngineChange = (engine) => {
    updateActive({
      engine,
      port: engine === 'timescaledb' ? 5432 : 1433,
      database: '',
      serverInfo: null,
    });
    setDatabasesById((prev) => ({ ...prev, [active.id]: [] }));
    setTestOk(null);
  };

  const saveConfigMutation = useMutation({
    mutationFn: ({ body, headers }) => axios.put('/api/admin/runtime-config', body, { headers }),
  });

  const handleSave = async () => {
    const headers = getAuthHeaders();
    if (!headers) {
      showError('No hay credenciales de sesión');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = connections.map((c) => ({
        ...c,
        password: c.password || undefined,
      }));
      const res = await saveConfigMutation.mutateAsync({
        body: { dataConnections: payload },
        headers,
      });
      if (res.data.success) {
        showSuccess('Conexiones guardadas');
        const list = (res.data.dataConnections || []).map((c) => ({
          ...emptyConnection(),
          ...c,
          password: '',
        }));
        setConnections(list);
        const def = list.find((c) => c.isDefault) || list[0];
        setActiveId(def?.id || null);
      } else {
        setError(res.data.error || 'Error al guardar');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Error al guardar';
      showError(msg);
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const dbs = active ? databasesById[active.id] || [] : [];

  const formContent = (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Conexiones de datos BI e instrucciones de compatibilidad SQL por servidor. El modelo de tablas, diagrama y
        ejemplos para la IA se configura en la pestaña <strong>Modelo de datos</strong>.
      </Typography>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {testOk && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {testOk}
        </Alert>
      )}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          <Paper variant="outlined" sx={{ p: 1, minWidth: 220, maxWidth: 280 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.5 }}>
              <Typography variant="subtitle2">Conexiones</Typography>
              <IconButton size="small" onClick={handleAdd} title="Agregar">
                <AddIcon fontSize="small" />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 1 }} />
            {connections.map((c) => (
              <Box
                key={c.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: c.id === activeId ? 'action.selected' : 'transparent',
                  cursor: 'pointer',
                }}
                onClick={() => setActiveId(c.id)}
              >
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setDefault(c.id); }} title="Predeterminada">
                  {c.isDefault ? <StarIcon fontSize="small" color="warning" /> : <StarBorderIcon fontSize="small" />}
                </IconButton>
                <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {c.name || c.host || c.id}
                </Typography>
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleRemove(c.id); }} disabled={connections.length <= 1}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Paper>

          {active && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Nombre"
                value={active.name}
                onChange={(e) => updateActive({ name: e.target.value })}
                fullWidth
                size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Motor</InputLabel>
                <Select label="Motor" value={active.engine} onChange={(e) => onEngineChange(e.target.value)}>
                  <MenuItem value="mssql">Microsoft SQL Server</MenuItem>
                  <MenuItem value="timescaledb">TimescaleDB (PostgreSQL)</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Host" value={active.host} onChange={(e) => updateActive({ host: e.target.value })} fullWidth size="small" />
              <TextField
                label="Puerto"
                type="number"
                value={active.port}
                onChange={(e) => updateActive({ port: parseInt(e.target.value || '0', 10) })}
                fullWidth
                size="small"
              />
              <TextField label="Usuario" value={active.user} onChange={(e) => updateActive({ user: e.target.value })} fullWidth size="small" />
              <TextField
                label="Contraseña"
                type="password"
                value={active.password}
                onChange={(e) => updateActive({ password: e.target.value })}
                fullWidth
                size="small"
                placeholder={active.passwordSet ? '•••••••• (dejar vacío para no cambiar)' : ''}
                helperText={active.passwordSet ? 'Ya hay una contraseña guardada' : ''}
              />
              {active.engine === 'mssql' ? (
                <>
                  <FormControlLabel
                    control={<Checkbox checked={Boolean(active.encrypt)} onChange={(e) => updateActive({ encrypt: e.target.checked })} />}
                    label="Encrypt"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={active.trustServerCertificate !== false}
                        onChange={(e) => updateActive({ trustServerCertificate: e.target.checked })}
                      />
                    }
                    label="Trust server certificate"
                  />
                </>
              ) : (
                <FormControlLabel
                  control={<Checkbox checked={Boolean(active.ssl)} onChange={(e) => updateActive({ ssl: e.target.checked })} />}
                  label="SSL"
                />
              )}
              <Button
                variant="outlined"
                onClick={() => handleTest(active)}
                disabled={testingId === active.id || listingId === active.id}
              >
                {testingId === active.id || listingId === active.id ? 'Probando…' : 'Probar y listar BDs'}
              </Button>
              <FormControl fullWidth size="small" disabled={dbs.length === 0 && !active.database}>
                <InputLabel>Base de datos default</InputLabel>
                <Select
                  label="Base de datos default"
                  value={active.database || ''}
                  onChange={(e) => updateActive({ database: e.target.value })}
                >
                  {active.database && !dbs.includes(active.database) ? (
                    <MenuItem value={active.database}>{active.database}</MenuItem>
                  ) : null}
                  {dbs.map((db) => (
                    <MenuItem key={db} value={db}>{db}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Bases permitidas en el selector (opcional)"
                value={active.allowedDatabases || ''}
                onChange={(e) => updateActive({ allowedDatabases: e.target.value })}
                fullWidth
                size="small"
                helperText="Separadas por coma. Vacío = todas las accesibles."
              />

              <Divider sx={{ my: 1 }} />

              {active.serverInfo?.detectedAt && (
                <Alert severity="info" sx={{ py: 0.5 }}>
                  <Typography variant="caption" component="div">
                    <strong>Motor detectado</strong> ({new Date(active.serverInfo.detectedAt).toLocaleString()}):
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ whiteSpace: 'pre-wrap' }}>
                    {String(active.serverInfo.fullVersion || active.serverInfo.version || '—').split('\n')[0]}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Versión del motor detectada al probar la conexión.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </Box>
      )}
    </>
  );

  const actions = (
    <Button onClick={handleSave} variant="contained" disabled={loading || saving}>
      {saving ? 'Guardando…' : 'Guardar conexiones'}
    </Button>
  );

  if (embedded) {
    return (
      <Box sx={{ p: 0 }}>
        {formContent}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>{actions}</Box>
      </Box>
    );
  }

  return formContent;
};

export default DataConnectionAdmin;
